import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { useTheme } from '../context/ThemeContext';
import RoleBasedNavigation from '../components/organization/RoleBasedNavigation';
import OrganizationBrand from '../components/organization/OrganizationBrand';
import OrgNotificationBell from '../components/organization/OrgNotificationBell';
import ModeSwitch from '../components/common/ModeSwitch';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useHeader } from '../context/HeaderContext';
import Avatar from '../components/common/Avatar';
import { 
  FiMenu, 
  FiX, 
  FiUser, 
  FiSettings, 
  FiLogOut, 
  FiGrid,
  FiUsers,
  FiFolder,
  FiBarChart2,
  FiDollarSign,
  FiBell,
  FiSearch,
  FiHome,
  FiPlus,
  FiTrendingUp,
  FiShield,
  FiHelpCircle,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiMoon,
  FiSun,
  FiCalendar,
  FiCheckSquare,
  FiFileText
} from 'react-icons/fi';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const HEADER_HEIGHT = 56;
// Mobile: full width + minimal horizontal inset; md+: prior max-width + breathing room
const CONTENT_MAX_WIDTH = 'max-w-full md:max-w-[96%] 2xl:max-w-[1600px]';
const CONTENT_PADDING_X =
  'px-2.5 sm:px-4 md:px-6 lg:px-8 pl-[max(0.625rem,env(safe-area-inset-left))] pr-[max(0.625rem,env(safe-area-inset-right))]';

const OrganizationLayout = ({ children }) => {
  const { currentUser, logout, getUserRoles } = useAuth();
  const { hasPermission, currentOrganization, isEnterpriseUser } = useOrganization();
  const {
    theme,
    organizationBranding,
    getOrganizationBranding,
    isOrganizationThemed,
    darkMode,
    toggleDarkMode
  } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { headerConfig } = useHeader();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isDraggingSidebar, setIsDraggingSidebar] = useState(false);
  
  const branding = organizationBranding || getOrganizationBranding();
  const userRoles = getUserRoles();
  const organizationIdentifier = currentOrganization?._id || currentOrganization?.id || currentOrganization?.slug || 'default';
  const sidebarStorageKey = `org_sidebar_collapsed_${organizationIdentifier}`;

  // Helper to convert hex to rgba
  const hexToRgba = (hex = '#000000', alpha = 1) => {
    const sanitized = hex.replace('#', '');
    if (sanitized.length !== 6) {
      return `rgba(0,0,0,${alpha})`;
    }
    const bigint = parseInt(sanitized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const accentColor = branding?.primaryColor || theme.colors.primary;
  const subtleHeaderBg = hexToRgba(accentColor, 0.04);
  const subtleHeaderBorder = hexToRgba(accentColor, 0.12);
  const workspaceLabel = 'Workspace';

  // Display job title when set, otherwise human-readable role (not raw orgRole like "Dept_manager")
  const getDisplayTitleOrRole = () => {
    const jobTitle = (currentUser?.jobTitle || '').trim();
    if (jobTitle) return jobTitle;
    const role = userRoles?.orgRole || currentUser?.orgRole || userRoles?.systemRole || currentUser?.systemRole;
    if (role === 'org_admin') {
      switch (currentUser?.orgAdminAccessLevel) {
        case 'full': return 'Full Admin';
        case 'limited': return 'Delegated Admin';
        case 'read_only': return 'Security Reviewer';
        default: return 'Full Admin';
      }
    }
    switch (role) {
      case 'dept_manager': return 'Department Manager';
      case 'team_lead': return 'Team Lead';
      case 'member': return 'Member';
      case 'admin': return 'Administrator';
      default: return role ? String(role).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Member';
    }
  };

  const rootClassNames = [
    'h-screen flex overflow-hidden org-layout'
  ];

  if (isOrganizationThemed) {
    rootClassNames.push('org-themed');
  }
  
  // Add dark mode class
  if (darkMode) {
    rootClassNames.push('dark bg-slate-900');
  } else if (!isOrganizationThemed) {
    rootClassNames.push('bg-gray-50');
  }

  const layoutStyle = {
    backgroundColor: darkMode 
      ? 'var(--org-background, #0F172A)' 
      : isOrganizationThemed 
        ? 'var(--org-background, var(--color-backgroundAlt))' 
        : undefined,
    color: darkMode 
      ? 'var(--org-text, #F1F5F9)' 
      : isOrganizationThemed 
        ? 'var(--org-text, var(--color-text))' 
        : undefined
  };

  // Determine header style from branding
  const headerThemeStyle = branding?.headerStyle || currentOrganization?.headerStyle || 'themed';
  const headerColor = branding?.headerColor || currentOrganization?.headerColor || accentColor;

  // Header styles with dark mode and organization theme support
  const headerStyles = darkMode
    ? {
        backgroundColor: 'var(--org-header-bg, #1E293B)',
        borderColor: 'var(--org-header-border, #334155)',
        color: 'var(--org-header-text, #F1F5F9)',
        '--org-accent': accentColor,
        '--header-text': '#F1F5F9',
        '--header-text-muted': '#94A3B8',
        minHeight: HEADER_HEIGHT
      }
    : isOrganizationThemed
      ? headerThemeStyle === 'themed' 
        ? {
            // Themed header - use primary color with subtle background
            backgroundColor: hexToRgba(accentColor, 0.08),
            borderColor: hexToRgba(accentColor, 0.15),
            color: 'var(--org-text, #1F2937)',
            '--org-accent': accentColor,
            '--header-text': 'var(--org-text, #1F2937)',
            '--header-text-muted': 'var(--org-text-muted, #6B7280)',
            minHeight: HEADER_HEIGHT
          }
        : headerThemeStyle === 'colored'
          ? {
              // Colored header - use primary color as background
              backgroundColor: accentColor,
              borderColor: hexToRgba(accentColor, 0.8),
              color: '#FFFFFF',
              '--org-accent': accentColor,
              '--header-text': '#FFFFFF',
              '--header-text-muted': 'rgba(255,255,255,0.7)',
              minHeight: HEADER_HEIGHT
            }
          : {
              // Default/white header
              backgroundColor: 'var(--org-header-bg, var(--org-surface, #FFFFFF))',
              borderColor: 'var(--org-header-border, var(--org-border, #E5E7EB))',
              color: 'var(--org-header-text, var(--org-text, #1F2937))',
              '--org-accent': accentColor,
              '--header-text': 'var(--org-text, #1F2937)',
              '--header-text-muted': 'var(--org-text-muted, #6B7280)',
              minHeight: HEADER_HEIGHT
            }
      : {
          backgroundColor: subtleHeaderBg,
          borderColor: subtleHeaderBorder,
          '--org-accent': accentColor,
          '--header-text': '#1F2937',
          '--header-text-muted': '#6B7280',
          minHeight: HEADER_HEIGHT
        };

  // Sidebar styles with dark mode support
  const sidebarStyles = darkMode
    ? {
        background: 'var(--org-sidebar-bg, #1E293B)',
        color: 'var(--org-sidebar-text, #F1F5F9)',
        borderColor: '#334155',
        '--sidebar-text': 'var(--org-sidebar-text, #F1F5F9)',
        '--sidebar-text-muted': 'var(--org-sidebar-muted, #94A3B8)',
        '--sidebar-hover-bg': 'rgba(255,255,255,0.08)',
        '--sidebar-active-bg': 'rgba(255,255,255,0.12)',
        '--sidebar-border': '#334155'
      }
    : isOrganizationThemed
      ? {
          background: 'var(--org-sidebar-bg, var(--org-surface, #FFFFFF))',
          color: 'var(--org-sidebar-text, var(--org-text, #111827))',
          borderColor: 'var(--org-border, #E5E7EB)',
          '--sidebar-text': 'var(--org-sidebar-text, #FFFFFF)',
          '--sidebar-text-muted': 'var(--org-sidebar-muted, rgba(255,255,255,0.7))',
          '--sidebar-hover-bg': 'rgba(255,255,255,0.1)',
          '--sidebar-active-bg': 'rgba(255,255,255,0.15)',
          '--sidebar-border': 'rgba(255,255,255,0.1)'
        }
      : {
          '--sidebar-text': '#1F2937',
          '--sidebar-text-muted': '#6B7280',
          '--sidebar-hover-bg': '#F3F4F6',
          '--sidebar-active-bg': '#E5E7EB',
          '--sidebar-border': '#E5E7EB'
        };

  // Breadcrumb generation (from local version)
  const ROUTE_LABELS = {
    dashboard: 'Dashboard',
    projects: 'Projects',
    teams: 'Teams',
    departments: 'Departments',
    analytics: 'Analytics',
    settings: 'Settings',
    billing: 'Billing',
    users: 'Users',
    calendar: 'Calendar',
    finance: 'Finance',
    marketing: 'Marketing',
    create: 'Create',
    edit: 'Edit',
    profile: 'Profile',
  };

  const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i;

  const RESOURCE_DETAIL_LABELS = {
    teams: 'Team',
    projects: 'Project',
    tasks: 'Task',
    departments: 'Department',
    users: 'User',
  };

  const formatSegment = (segment, previousSegment) => {
    if (OBJECT_ID_REGEX.test(segment)) {
      return RESOURCE_DETAIL_LABELS[previousSegment?.toLowerCase()] || 'Details';
    }

    const key = segment.toLowerCase();
    if (ROUTE_LABELS[key]) return ROUTE_LABELS[key];
    return segment
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const breadcrumbs = useMemo(() => {
    const trimmedPath = location.pathname.replace(/^\/org\/?/, '');
    const segments = trimmedPath.split('/').filter(Boolean);

    const crumbs = [
      { label: workspaceLabel, path: '/org/dashboard' }
    ];

    if (!segments.length) {
      crumbs.push({ label: 'Dashboard', path: '/org/dashboard' });
      return crumbs;
    }

    let currentPath = '/org';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const previousSegment = index > 0 ? segments[index - 1] : null;
      crumbs.push({ label: formatSegment(segment, previousSegment), path: currentPath });
    });

    return crumbs;
  }, [location.pathname, workspaceLabel]);

  const displayBreadcrumbs = useMemo(() => {
    if (!headerConfig.pageTitle || breadcrumbs.length === 0) return breadcrumbs;
    const updated = [...breadcrumbs];
    updated[updated.length - 1] = {
      ...updated[updated.length - 1],
      label: headerConfig.pageTitle,
    };
    return updated;
  }, [breadcrumbs, headerConfig.pageTitle]);

  const currentPageCrumb = displayBreadcrumbs[displayBreadcrumbs.length - 1];

  const PAGE_CONTEXT_ICONS = {
    team: FiUsers,
    teams: FiUsers,
    project: FiFolder,
    projects: FiFolder,
    task: FiCheckSquare,
    tasks: FiCheckSquare,
    'tasks-timeline': FiCalendar,
    analytics: FiBarChart2,
    reports: FiFileText,
  };

  const PAGE_CONTEXT_LABELS = {
    team: 'Team',
    teams: 'Teams',
    project: 'Project',
    projects: 'Projects',
    task: 'Task',
    tasks: 'Tasks',
    'tasks-timeline': 'Timeline',
    analytics: 'Analytics',
    reports: 'Reports',
  };

  const resolvePageContext = (pathname, explicitContext) => {
    if (explicitContext) return explicitContext;
    if (!pathname.startsWith('/org/')) return null;
    if (pathname.startsWith('/org/teams')) {
      return pathname === '/org/teams' || pathname === '/org/teams/' ? 'teams' : 'team';
    }
    if (pathname.startsWith('/org/projects')) {
      return pathname === '/org/projects' || pathname === '/org/projects/' || pathname.endsWith('/create')
        ? 'projects'
        : 'project';
    }
    if (pathname.startsWith('/org/tasks')) {
      if (pathname.includes('/timeline')) return 'tasks-timeline';
      return pathname === '/org/tasks' || pathname === '/org/tasks/' || pathname.endsWith('/create')
        ? 'tasks'
        : 'task';
    }
    if (pathname.startsWith('/org/analytics')) return 'analytics';
    if (pathname.startsWith('/org/reports')) return 'reports';
    return null;
  };

  const activePageContext = useMemo(
    () => resolvePageContext(location.pathname, headerConfig.pageContext),
    [location.pathname, headerConfig.pageContext]
  );

  const ContextualHeaderIcon = ({ className = '' }) => {
    const Icon = activePageContext ? PAGE_CONTEXT_ICONS[activePageContext] : null;
    if (!Icon) return null;
    const label = headerConfig.pageTitle || PAGE_CONTEXT_LABELS[activePageContext] || 'Page';

    return (
      <span
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${className}`}
        style={{
          backgroundColor: darkMode ? 'rgba(255,255,255,0.12)' : (isOrganizationThemed ? 'rgba(255,255,255,0.2)' : 'rgba(59,130,246,0.12)'),
          color: 'var(--header-text, #1F2937)',
        }}
        role="img"
        aria-label={label}
      >
        <Icon className="h-4 w-4" />
      </span>
    );
  };

  // Sidebar collapse persistence
  useEffect(() => {
    if (typeof window === 'undefined' || !sidebarStorageKey) return;
    const storedValue = window.localStorage.getItem(sidebarStorageKey);
    if (storedValue !== null) {
      setSidebarCollapsed(storedValue === 'true');
    }
  }, [sidebarStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !sidebarStorageKey) return;
    window.localStorage.setItem(sidebarStorageKey, sidebarCollapsed ? 'true' : 'false');
  }, [sidebarCollapsed, sidebarStorageKey]);

  const toggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  // Drag to resize sidebar
  const handleSidebarDragStart = useCallback((event) => {
    event.preventDefault();
    setIsDraggingSidebar(true);
  }, []);

  const handleSidebarDragMove = useCallback((event) => {
    if (!isDraggingSidebar) return;
    const currentX = event.clientX;

    if (sidebarCollapsed) {
      if (currentX > SIDEBAR_COLLAPSED_WIDTH + 48) {
        toggleSidebarCollapse();
        setIsDraggingSidebar(false);
      }
    } else if (currentX < SIDEBAR_WIDTH / 2) {
      toggleSidebarCollapse();
      setIsDraggingSidebar(false);
    }
  }, [isDraggingSidebar, sidebarCollapsed, toggleSidebarCollapse]);

  const handleSidebarDragEnd = useCallback(() => {
    if (!isDraggingSidebar) return;
    setIsDraggingSidebar(false);
  }, [isDraggingSidebar]);

  useEffect(() => {
    if (!isDraggingSidebar || typeof document === 'undefined') return;

    document.addEventListener('mousemove', handleSidebarDragMove);
    document.addEventListener('mouseup', handleSidebarDragEnd);

    if (document.body) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleSidebarDragMove);
      document.removeEventListener('mouseup', handleSidebarDragEnd);
      if (document.body) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, [isDraggingSidebar, handleSidebarDragMove, handleSidebarDragEnd]);

  const renderBrandGlyph = () => {
    if (branding?.logo) {
      return (
        <img
          src={branding.logo}
          alt={`${branding.name || 'Organization'} logo`}
          className="w-10 h-10 rounded-xl object-cover"
        />
      );
    }

    const fallbackLetter = (branding?.name || currentOrganization?.name || 'O').charAt(0).toUpperCase();
    return (
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-semibold"
        style={{ backgroundColor: accentColor }}
      >
        {fallbackLetter}
      </div>
    );
  };

  /**
   * Navigation Items - Simplified Structure (No Duplicates)
   * 
   * This matches the new RoleBasedNavigation structure:
   * - WORK: Projects, Tasks, Teams (top-level, filtered by access)
   * - INSIGHTS: Analytics, Reports (managers+)
   * - APPS: Finance, App Store
   * - ADMINISTRATION: Departments, Users, Settings (admins)
   */
  const navigationItems = [
    // WORKSPACE
    {
      name: 'Dashboard',
      href: '/org/dashboard',
      icon: FiHome,
      current: location.pathname === '/org/dashboard' || location.pathname === '/org',
      show: true,
      section: 'workspace'
    },
    
    // WORK - Daily work items (everyone)
    {
      name: 'Projects',
      href: '/org/projects',
      icon: FiFolder,
      current: location.pathname.startsWith('/org/projects'),
      show: hasPermission('canViewProjects'),
      section: 'work'
    },
    {
      name: 'Tasks',
      href: '/org/tasks',
      icon: FiFolder,
      current: location.pathname === '/org/tasks' || location.pathname.startsWith('/org/tasks/') && !location.pathname.includes('/timeline'),
      show: hasPermission('canViewTasks'),
      section: 'work'
    },
    {
      name: 'Timeline',
      href: '/org/tasks/timeline',
      icon: FiCalendar, // Calendar icon for Gantt/timeline view
      current: location.pathname === '/org/tasks/timeline',
      show: hasPermission('canViewTasks') || true, // Always show for org users
      section: 'work'
    },
    {
      name: 'Teams',
      href: '/org/teams',
      icon: FiUsers,
      current: location.pathname.startsWith('/org/teams'),
      show: hasPermission('canViewTeams'),
      section: 'work'
    },
    
    // INSIGHTS - Analytics & Reports (managers+)
    {
      name: 'Analytics',
      href: '/org/analytics',
      icon: FiBarChart2,
      current: location.pathname.startsWith('/org/analytics'),
      show: hasPermission('canViewAnalytics'),
      section: 'insights'
    },
    {
      name: 'Reports',
      href: '/org/reports',
      icon: FiTrendingUp,
      current: location.pathname.startsWith('/org/reports'),
      show: hasPermission('canViewAnalytics'),
      section: 'insights'
    },
    
    // APPS - Installed applications
    {
      name: 'Finance',
      href: '/org/finance',
      icon: FiDollarSign,
      current: location.pathname.startsWith('/org/finance'),
      show: true, // Everyone can access finance
      section: 'apps'
    },
    
    // ADMINISTRATION - Management (dept_manager+, org_admin for all)
    {
      name: 'Departments',
      href: '/org/departments',
      icon: FiGrid,
      current: location.pathname.startsWith('/org/departments'),
      show: hasPermission('canManageDepartments') || hasPermission('canManageDepartment'),
      section: 'admin'
    },
    {
      name: 'Users',
      href: '/org/users',
      icon: FiUsers,
      current: location.pathname.startsWith('/org/users'),
      show: hasPermission('canInviteUsers') || hasPermission('canManageUsers'),
      section: 'admin'
    },
    {
      name: 'Billing',
      href: '/org/billing',
      icon: FiDollarSign,
      current: location.pathname.startsWith('/org/billing'),
      show: hasPermission('canManageOrganization'),
      section: 'admin'
    },
    {
      name: 'Settings',
      href: '/org/settings',
      icon: FiSettings,
      current: location.pathname.startsWith('/org/settings'),
      show: hasPermission('canManageOrganization'),
      section: 'admin'
    }
  ].filter(item => item.show);

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const Sidebar = ({ mobile = false }) => {
    const effectiveCollapsed = !mobile && sidebarCollapsed;
    const currentWidth = effectiveCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

    return (
      <div
        className={`relative flex flex-col h-full border-r transition-[width] duration-200 ease-in-out org-sidebar ${
          mobile ? 'w-64' : ''
        }`}
        style={{
          ...(!mobile ? { width: currentWidth } : {}),
          ...sidebarStyles
        }}
      >
        {/* Organization Brand */}
        <div
          className={`relative flex items-center border-b ${
            effectiveCollapsed ? 'justify-center px-2 py-3' : 'justify-between px-5 py-3'
          }`}
          style={{
            minHeight: HEADER_HEIGHT,
            borderColor: isOrganizationThemed ? 'var(--org-border, #E5E7EB)' : undefined
          }}
        >
          {effectiveCollapsed ? (
            renderBrandGlyph()
          ) : (
            <OrganizationBrand size="md" showName variant="minimal" />
          )}

          {!mobile && (
            <button
              onClick={toggleSidebarCollapse}
              className="hidden lg:flex items-center justify-center p-1.5 transition-all absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-100"
              style={{ 
                color: 'var(--sidebar-text-muted)', 
                opacity: effectiveCollapsed ? 0 : 0.8,
                pointerEvents: 'auto'
              }}
              title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {effectiveCollapsed ? (
                <FiChevronRight className="w-4 h-4" />
              ) : (
                <FiChevronLeft className="w-4 h-4" />
              )}
            </button>
          )}
          {mobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md transition-colors"
              style={{ color: 'var(--sidebar-text)' }}
            >
              <FiX className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Mode Switcher */}
        {!effectiveCollapsed && (
          <div
            className="px-5 py-3 border-b"
            style={{ borderColor: isOrganizationThemed ? 'var(--org-border, #E5E7EB)' : undefined }}
          >
            <ModeSwitch variant="compact" className="w-full" />
          </div>
        )}

        {/* Navigation - Scrollable with hidden scrollbar */}
        <div className={`flex-1 py-3 ${effectiveCollapsed ? 'px-1' : 'px-3'} overflow-y-auto`} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <style>{`.sidebar-scroll::-webkit-scrollbar { display: none; }`}</style>
          <RoleBasedNavigation
            isMobile={mobile}
            isCollapsed={effectiveCollapsed}
            onItemClick={() => setSidebarOpen(false)}
          />
        </div>

        {/* Footer */}
        <div
          className={`border-t ${effectiveCollapsed ? 'px-0 py-3 flex flex-col items-center gap-2 text-[10px]' : 'px-5 py-3'}`}
          style={{ 
            borderColor: 'var(--sidebar-border)',
            color: 'var(--sidebar-text-muted)'
          }}
        >
          {effectiveCollapsed ? (
            <>
              <span className="font-semibold uppercase tracking-wide">
                {isOrganizationThemed ? 'Ent' : 'Plt'}
              </span>
              <Link to="/help" className="p-2 rounded-full transition-colors" style={{ color: 'var(--sidebar-text-muted)' }} title="Help Center">
                <FiHelpCircle className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <div className="flex items-center justify-between text-xs" style={{ color: 'var(--sidebar-text-muted)' }}>
              <span>{isOrganizationThemed ? 'Enterprise' : 'Platform'}</span>
              <Link to="/help" style={{ color: 'var(--sidebar-text-muted)' }}>
                <FiHelpCircle className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>

        {/* Drag Handle */}
        {!mobile && (
          <div
            className="absolute top-0 right-0 h-full w-1 cursor-col-resize group"
            onMouseDown={handleSidebarDragStart}
            title={effectiveCollapsed ? 'Drag right to expand sidebar' : 'Drag left to collapse sidebar'}
          >
            <span
              className={`absolute inset-y-0 right-0 w-0.5 transition-opacity ${
                isDraggingSidebar
                  ? 'bg-blue-400 opacity-100'
                  : effectiveCollapsed
                    ? 'bg-blue-400 opacity-30 group-hover:opacity-80'
                    : 'bg-blue-300 opacity-0 group-hover:opacity-70'
              }`}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={rootClassNames.join(' ')}
      style={layoutStyle}
      data-org-themed={isOrganizationThemed || undefined}
    >
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <Sidebar />
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top navigation with breadcrumbs */}
        <header
          className="sticky top-0 z-40 border-b org-header"
          style={headerStyles}
        >
          <div className="h-14">
            <div className={`w-full ${CONTENT_MAX_WIDTH} mx-auto h-full ${CONTENT_PADDING_X} flex items-center gap-4`}>
              {/* Left: Mobile menu + Breadcrumbs */}
              <div className="flex min-w-0 flex-1 items-center gap-2 lg:flex-initial lg:gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden shrink-0 rounded-full p-2 transition-colors"
                  style={{ color: 'var(--header-text-muted, #6B7280)' }}
                  aria-label="Open navigation"
                >
                  <FiMenu className="w-5 h-5" />
                </button>

                {/* Mobile: section icon only for teams / projects / tasks */}
                <div className="min-w-0 flex-1 lg:hidden">
                  {activePageContext ? (
                    <ContextualHeaderIcon />
                  ) : (
                    <span
                      className="block truncate text-base font-semibold leading-tight"
                      style={{ color: 'var(--header-text, #1F2937)' }}
                    >
                      {currentPageCrumb?.label}
                    </span>
                  )}
                </div>

                {/* Desktop: full breadcrumb trail */}
                <nav
                  className="hidden min-w-0 items-center gap-2 text-sm lg:flex"
                  aria-label="Breadcrumb"
                >
                  {displayBreadcrumbs.map((crumb, index) => (
                    <React.Fragment key={`${crumb.path}-${crumb.label}`}>
                      {index === displayBreadcrumbs.length - 1 ? (
                        <span
                          className="truncate font-semibold"
                          style={{ color: 'var(--header-text, #1F2937)' }}
                        >
                          {crumb.label}
                        </span>
                      ) : (
                        <Link
                          to={crumb.path}
                          className="truncate transition hover:opacity-80"
                          style={{ color: 'var(--header-text-muted, #6B7280)' }}
                        >
                          {crumb.label}
                        </Link>
                      )}
                      {index !== displayBreadcrumbs.length - 1 && (
                        <span style={{ color: 'var(--header-text-muted, #9CA3AF)', opacity: 0.5 }}>/</span>
                      )}
                    </React.Fragment>
                  ))}
                </nav>
              </div>

              {/* Center: Search */}
              <div className="flex-1 hidden md:block">
                <div className="relative max-w-xl">
                  <FiSearch 
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" 
                    style={{ color: 'var(--header-text-muted, #9CA3AF)' }}
                  />
                  <input
                    type="search"
                    placeholder="Search projects, teams, docs"
                    className="w-full h-10 rounded-lg pl-10 pr-4 text-sm focus:ring-1 focus:ring-[var(--org-accent)]"
                    style={{
                      backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : (isOrganizationThemed ? 'rgba(255,255,255,0.7)' : 'rgba(243,244,246,1)'),
                      color: 'var(--header-text, #111827)',
                      borderColor: darkMode ? 'rgba(255,255,255,0.2)' : (isOrganizationThemed ? 'rgba(0,0,0,0.1)' : '#E5E7EB'),
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    }}
                  />
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2 ml-auto">
                <OrgNotificationBell />

                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-full transition-colors"
                  style={{ 
                    color: 'var(--header-text-muted, #6B7280)',
                  }}
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode ? (
                    <FiSun className="w-5 h-5" />
                  ) : (
                    <FiMoon className="w-5 h-5" />
                  )}
                </button>

                <button
                  onClick={() => navigate('/help')}
                  className="p-2 rounded-full"
                  style={{ 
                    color: 'var(--header-text-muted, #6B7280)',
                  }}
                  aria-label="Open help center"
                >
                  <FiHelpCircle className="w-5 h-5" />
                </button>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-xl border border-transparent"
                    style={{ 
                      backgroundColor: darkMode
                        ? 'rgba(255,255,255,0.1)'
                        : headerThemeStyle === 'colored'
                          ? 'rgba(0,0,0,0.2)'
                          : isOrganizationThemed
                            ? 'rgba(255,255,255,0.85)'
                            : 'transparent',
                    }}
                  >
                    <Avatar
                      user={currentUser}
                      size="sm"
                      className="h-9 w-9 shrink-0 cursor-default text-sm hover:opacity-100"
                    />
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium truncate" style={{ color: headerThemeStyle === 'colored' ? '#FFFFFF' : 'var(--header-text, #111827)' }}>{currentUser?.name}</p>
                      <p className="text-xs truncate" style={{ color: headerThemeStyle === 'colored' ? 'rgba(255,255,255,0.9)' : 'var(--header-text-muted, #6B7280)' }}>{getDisplayTitleOrRole()}</p>
                    </div>
                  </button>

                  {userMenuOpen && (
                    <div 
                      className="absolute right-0 mt-3 w-56 rounded-xl z-50 border border-gray-300 dark:border-gray-600 shadow-xl shadow-gray-300/40 dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.35)]"
                      style={{
                        backgroundColor: darkMode ? '#1F2937' : '#FFFFFF'
                      }}
                    >
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <p className="text-sm font-semibold" style={{ color: darkMode ? '#F9FAFB' : '#111827' }}>{currentUser?.name}</p>
                        <p className="text-xs" style={{ color: darkMode ? '#9CA3AF' : '#6B7280' }}>{currentUser?.email}</p>
                        <div className="mt-2 text-[11px]" style={{ color: darkMode ? '#9CA3AF' : '#6B7280' }}>
                            {getDisplayTitleOrRole()}
                          </div>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/org/profile"
                          className="flex items-center px-4 py-2 text-sm"
                          style={{ color: darkMode ? '#D1D5DB' : '#374151' }}
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <FiUser className="w-4 h-4 mr-3" />
                          Profile
                        </Link>
                        <Link
                          to="/org/settings"
                          className="flex items-center px-4 py-2 text-sm"
                          style={{ color: darkMode ? '#D1D5DB' : '#374151' }}
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <FiSettings className="w-4 h-4 mr-3" />
                          Settings
                        </Link>
                        <hr className="my-1 border-t border-gray-200 dark:border-gray-600" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <FiLogOut className="w-4 h-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area - same max-width and horizontal padding on all org pages */}
        <main 
          className="flex-1 relative overflow-y-auto focus:outline-none"
          style={{
            backgroundColor: darkMode ? '#111827' : undefined,
            color: darkMode ? '#F9FAFB' : undefined
          }}
        >
          <div className={`mx-auto w-full ${CONTENT_MAX_WIDTH} ${CONTENT_PADDING_X} py-6 min-h-full`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default OrganizationLayout;
